// Shared helpers for the analytics dashboard: chart.js registration, a colour palette,
// and small formatters. Importing this module registers the chart.js pieces we use, so any
// component that renders a <Doughnut/> only needs to import from react-chartjs-2.
import {
  Chart as ChartJS,
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend
} from 'chart.js'

ChartJS.register(
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend
)

// Ordered, distinct palette reused across every breakdown so colours stay stable.
export const PALETTE = [
  '#1677ff', // blue
  '#52c41a', // green
  '#faad14', // gold
  '#fa8c16', // orange
  '#13c2c2', // cyan
  '#722ed1', // purple
  '#eb2f96', // magenta
  '#f5222d', // red
  '#8c8c8c' // grey
]

export const colorAt = index => PALETTE[index % PALETTE.length]

// Period options for the dashboard filter (value matches the w-api ?period= shortcut).
export const PERIOD_OPTIONS = [
  { value: 'all', label: 'All time' },
  { value: 'today', label: 'Last 24 hours' },
  { value: 'week', label: 'Last 7 days' },
  { value: 'month', label: 'Last 30 days' },
  { value: 'year', label: 'Last 12 months' }
]

export const formatMoney = (amount, currency = 'GHS') => {
  const n = Number(amount || 0)
  return `${currency} ${n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`
}

// Build the chart.js dataset for a section's segments. Zero-count segments are dropped from
// the ring (but still shown in the legend) so the doughnut isn't cluttered with empty slices.
export const doughnutData = segments => {
  const shown = (segments || []).filter(s => s.count > 0)
  return {
    labels: shown.map(s => s.label),
    datasets: [
      {
        data: shown.map(s => s.count),
        backgroundColor: shown.map((s, i) => colorAt(i)),
        borderWidth: 1
      }
    ]
  }
}

export const doughnutOptions = {
  responsive: true,
  maintainAspectRatio: false,
  cutout: '62%',
  plugins: {
    legend: { display: false },
    tooltip: {
      callbacks: {
        label: ctx => `${ctx.label}: ${ctx.parsed}`
      }
    }
  }
}

// Build a grouped bar dataset from a { labels, series:[{key,label,data}] } payload (e.g. the
// activity-by-weekday analytics). Each series becomes one coloured group of bars.
export const barData = payload => {
  const labels = (payload && payload.labels) || []
  const series = (payload && payload.series) || []
  return {
    labels,
    datasets: series.map((s, i) => ({
      label: s.label,
      data: s.data || [],
      backgroundColor: colorAt(i),
      borderRadius: 4,
      maxBarThickness: 34
    }))
  }
}

export const barOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: 'bottom' },
    tooltip: { callbacks: { label: ctx => `${ctx.dataset.label}: ${ctx.parsed.y}` } }
  },
  scales: {
    x: { grid: { display: false } },
    y: { beginAtZero: true, ticks: { precision: 0 } }
  }
}
